import React, { useState, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import {
  Button, Modal, TextField, AlertV2, Link,
} from 'gatorcomponents';
import { IconCheck, IconClose } from '@/icons';
import * as Styles from './ChangePasswordModal.styles';
import useLocale from '@/hooks/useLocale';

const ChangePasswordModal = ({
  onClose, onConfirm, newInstallation, isLoading,
}) => {
  const theme = useContext(ThemeContext);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: locale } = productDetailsLocale;

  const validatePassword = (password) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasMinLength = password.length >= 8;

    return {
      hasUppercaseLowercase: hasUppercase && hasLowercase,
      hasNumber,
      hasSymbol,
      hasMinLength,
    };
  };

  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  const passwordValidation = validatePassword(newPassword);
  const allRequirementsMet = passwordValidation.hasUppercaseLowercase
    && passwordValidation.hasNumber
    && passwordValidation.hasSymbol
    && passwordValidation.hasMinLength
    && passwordsMatch;

  const handleSave = () => {
    if (allRequirementsMet) {
      onConfirm && onConfirm(newPassword, confirmPassword);
    }
  };

  const requirements = [
    {
      key: 'uppercaseLowercase',
      label: locale.infoSsh.changePasswordModal.requirementUppercaseLowercase,
      isValid: passwordValidation.hasUppercaseLowercase,
    },
    {
      key: 'number',
      label: locale.infoSsh.changePasswordModal.requirementNumber,
      isValid: passwordValidation.hasNumber,
    },
    {
      key: 'symbol',
      label: locale.infoSsh.changePasswordModal.requirementSymbol,
      isValid: passwordValidation.hasSymbol,
    },
    {
      key: 'length',
      label: locale.infoSsh.changePasswordModal.requirementLength,
      isValid: passwordValidation.hasMinLength,
    },
    {
      key: 'match',
      label: locale.infoSsh.changePasswordModal.requirementMatch,
      isValid: passwordsMatch,
    },
  ];

  return (
    <Modal
      onClose={onClose}
      maxWidth={500}
      title={newInstallation ? locale.infoSsh.changePasswordModal.newInstallation.title : locale.infoSsh.changePasswordModal.title}
      testId="change-password-modal"
    >
      <Styles.Container>
        <Styles.Content>
          <Styles.FieldsContainer>
            {newInstallation && (
              <Styles.FieldDescription>
                {locale.infoSsh.changePasswordModal.newInstallation.description}
              </Styles.FieldDescription>
            )}
            <Styles.FieldWrapper>
              <TextField
                label={locale.infoSsh.changePasswordModal.newPassword}
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                testId="new-password-field"
              />
            </Styles.FieldWrapper>

            <Styles.FieldWrapper>
              <TextField
                label={locale.infoSsh.changePasswordModal.confirmPassword}
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                testId="confirm-password-field"
              />
            </Styles.FieldWrapper>
          </Styles.FieldsContainer>

          <Styles.RequirementsContainer>
            <Styles.RequirementsTitle>
              {locale.infoSsh.changePasswordModal.passwordRequirements}
            </Styles.RequirementsTitle>
            <Styles.RequirementsList>
              {requirements.map(requirement => (
                <Styles.RequirementItem key={requirement.key} isValid={requirement.isValid}>
                  {requirement.isValid ? (
                    <IconCheck size={16} color={theme.v2.colors.feedback.activePure} />
                  ) : (
                    <IconClose size={16} color={theme.v2.colors.feedback.problemPure} />
                  )}
                  <Styles.RequirementText isValid={requirement.isValid}>
                    {requirement.label}
                  </Styles.RequirementText>
                </Styles.RequirementItem>
              ))}
            </Styles.RequirementsList>
          </Styles.RequirementsContainer>

          <Styles.AlertMessage>
            <AlertV2
              context="info"
              description={(
                <>
                  {locale.infoSsh.changePasswordModal.alertMessage.text}
                  {' '}
                  <Link
                    href={locale.infoSsh.changePasswordModal.alertMessage.link.href}
                    target="_blank"
                    text={locale.infoSsh.changePasswordModal.alertMessage.link.text}
                  />
                </>
              )}
              withoutBackground={false}
              testId="password-generator-alert"
            />
          </Styles.AlertMessage>
        </Styles.Content>

        <Styles.Actions>
          <Button
            label={locale.infoSsh.changePasswordModal.cancel}
            variant="tertiary"
            size="large"
            onClick={onClose}
            testId="change-password-modal-cancel-button"
          />
          <Button
            label={locale.infoSsh.changePasswordModal.save}
            variant="primary"
            size="large"
            onClick={handleSave}
            disabled={!allRequirementsMet || isLoading}
            testId="change-password-modal-save-button"
            loading={isLoading}
          />
        </Styles.Actions>
      </Styles.Container>
    </Modal>
  );
};

export default ChangePasswordModal;
